(() => {
  const pageSelector = '[data-cielo-product-page]';
  const keySwitchSelector = '.gpo-app select[name="keyset16"], .gpo-app select[aria-label="キースイッチセット"]';
  const keySwitchRecommendedLead = '標準スイッチよりも操作性を高めたい方におすすめです。より軽快な入力や好みの打鍵感に合わせて、最適なキースイッチを選べます。';
  const legacyKeySwitchHelpTexts = new Set([
    '本体と一緒に購入するキースイッチを選択できます。',
    '本体と一緒に購入する場合は、下から選択してください。'
  ]);

  const legacyFeatureDefinitions = [
    { pattern: /高剛性(?:の)?アルミ(?:合金)?フレーム|アルミ(?:合金)?フレーム(?:[^。]{0,30})(?:採用|搭載)/, label: '高剛性アルミフレーム', icon: 'shield' },
    { pattern: /マグネット(?:式)?(?:天板|トッププレート)/, label: 'マグネット式トッププレート', icon: 'layers' },
    { pattern: /ホットスワップ/, label: 'ホットスワップ対応', icon: 'switch' },
    { pattern: /トーナメントロック(?:スイッチ)?(?:[^。]{0,50})搭載/, label: 'トーナメントロック', icon: 'lock' },
    { pattern: /OLED(?:スクリーン|ディスプレイ)(?:[^。]{0,50})搭載/, label: 'OLEDディスプレイ', icon: 'display' },
    { pattern: /EXTポート(?:[^。]{0,20})搭載/, label: 'EXTポート搭載', icon: 'port' }
  ];

  const featureIconRules = [
    { pattern: /8000\s*Hz|ポーリング(?:レート)?|低遅延|高速応答|応答速度/i, icon: 'speed' },
    { pattern: /Bluetooth|2\.4\s*G(?:Hz)?|ワイヤレス|無線|接続(?:方式|モード)|3モード/i, icon: 'wireless' },
    { pattern: /OLED|ディスプレイ|スクリーン|表示/i, icon: 'display' },
    { pattern: /三和(?:電子)?|ジョイスティック|レバー/i, icon: 'joystick' },
    { pattern: /(?:\d+|複数)ボタン|ボタン(?:配置|入力|レイアウト)|入力マッピング|ボタンマッピング/i, icon: 'buttons' },
    { pattern: /マグネット|フェイスプレート|天板|トッププレート|プレート交換/i, icon: 'layers' },
    { pattern: /ホットスワップ|キースイッチ|スイッチ交換/i, icon: 'switch' },
    { pattern: /トーナメント|ロック|誤入力防止/i, icon: 'lock' },
    { pattern: /EXTポート|USBポート|拡張ポート|外部ポート/i, icon: 'port' },
    { pattern: /RGB|ライティング|発光/i, icon: 'lighting' },
    { pattern: /収納|キャリーハンドル|持ち運び|携帯/i, icon: 'storage' },
    { pattern: /アルミ|剛性|フレーム|金属筐体|筐体/i, icon: 'shield' },
    { pattern: /対応機種|互換|PS5|PS4|Xbox|Switch|PC対応/i, icon: 'compatibility' }
  ];

  const switchDetails = [
    {
      pattern: /^(なし|追加しない)$/i,
      shortName: '追加しない',
      description: '本体標準のスイッチ構成で購入します。',
      parameters: ['標準構成'],
      imageKey: 'none'
    },
    {
      pattern: /パンダ軸\s*V?1/i,
      shortName: 'パンダ軸 V1',
      description: '軽い押下圧と静かな打鍵感。初めてのカスタムにも選びやすいタイプ。',
      parameters: ['押下圧 30gf', '動作点 1.2mm', '総ストローク 2.8mm'],
      imageKey: 'panda'
    },
    {
      pattern: /パンダ軸\s*V?2/i,
      shortName: 'パンダ軸 V2',
      description: 'V1より適度な重さ。反応速度と押しやすさのバランスを重視。',
      parameters: ['押下圧 40gf', '動作点 0.8mm', '総ストローク 2.0mm'],
      imageKey: 'panda'
    },
    {
      pattern: /Saker\s*mini/i,
      shortName: 'Saker mini',
      description: '短いストロークで素早く入力。軽快な操作感を求める方向け。',
      parameters: ['押下圧 37gf', '動作点 0.8mm', '総ストローク 1.8mm'],
      imageKey: 'saker'
    }
  ];

  const negativeFeaturePattern = /(?:非対応|未対応|非搭載|未搭載|(?:対応|搭載)[^。]{0,12}(?:いない|いません|おりません|しない|ない|ありません|ございません)|(?:では|じゃ)(?:ない|ありません|ございません))/;

  function hasAffirmativeFeature(text, pattern) {
    const matcher = new RegExp(pattern.source, pattern.flags.replace('g', '') + 'g');
    return Array.from(text.matchAll(matcher)).some((match) => {
      const start = Math.max(0, match.index - 14);
      const end = Math.min(text.length, match.index + match[0].length + 18);
      return !negativeFeaturePattern.test(text.slice(start, end));
    });
  }

  function iconMarkup(name) {
    const paths = {
      feature: '<path d="m12 3 1.6 4.2L18 9l-4.4 1.8L12 15l-1.6-4.2L6 9l4.4-1.8L12 3Z"/><path d="m18.5 15 .9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1Z"/>',
      speed: '<path d="M3 12h4l2.5-6 4 12 2.5-6H21"/>',
      wireless: '<path d="M5 9a10 10 0 0 1 14 0M8 12a6 6 0 0 1 8 0M11 15a2 2 0 0 1 2 0"/><circle cx="12" cy="18" r="1"/>',
      joystick: '<circle cx="12" cy="6" r="3"/><path d="M12 9v6M7 15h10l2 6H5l2-6Z"/>',
      buttons: '<circle cx="8" cy="8" r="2"/><circle cx="16" cy="8" r="2"/><circle cx="8" cy="16" r="2"/><circle cx="16" cy="16" r="2"/>',
      shield: '<path d="M12 3 5 6v5c0 4.4 2.8 8.1 7 9.5 4.2-1.4 7-5.1 7-9.5V6l-7-3Z"/><path d="m9 12 2 2 4-5"/>',
      layers: '<path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/>',
      switch: '<path d="M7 7h10v10H7z"/><path d="M4 9V5a1 1 0 0 1 1-1h4M20 15v4a1 1 0 0 1-1 1h-4"/><path d="m7 4 2 0 0 2M17 20h-2v-2"/>',
      lock: '<rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
      display: '<rect x="3" y="5" width="18" height="13" rx="2"/><path d="M8 21h8M12 18v3"/>',
      port: '<path d="M8 7V4m8 3V4M6 7h12v5a6 6 0 0 1-12 0V7Z"/><path d="M12 18v3"/>',
      lighting: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
      storage: '<path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M3 12h18M10 12v2h4v-2"/>',
      compatibility: '<path d="M8 9h8a5 5 0 0 1 4.8 6.5l-.7 2.2a2 2 0 0 1-3.2 1l-2.2-1.7H9.3l-2.2 1.7a2 2 0 0 1-3.2-1l-.7-2.2A5 5 0 0 1 8 9Z"/><path d="M7 13v4M5 15h4M16 14h.01M18 16h.01"/>'
    };
    return `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${paths[name] || paths.feature}</svg>`;
  }

  function featureIconFor(label) {
    return featureIconRules.find((rule) => rule.pattern.test(label))?.icon || 'feature';
  }

  function structuredFeatures(description) {
    const seen = new Set();
    return Array.from(description.querySelectorAll('.cielo-prod-feature-item'))
      .map((item) => item.querySelector('h1, h2, h3, h4, h5, h6')?.textContent.replace(/\s+/g, ' ').trim())
      .filter((label) => {
        if (!label || seen.has(label)) return false;
        seen.add(label);
        return true;
      })
      .map((label) => ({ label, icon: featureIconFor(label) }));
  }

  function legacyFeatures(description) {
    const sourceText = description.textContent.replace(/\s+/g, ' ');
    return legacyFeatureDefinitions.filter((feature) => hasAffirmativeFeature(sourceText, feature.pattern));
  }

  function syncVariantTags(page) {
    const picker = page.querySelector('[data-cielo-variant-picker]');
    if (!picker) return;

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      fieldset.querySelectorAll('input[type="radio"][data-cielo-variant-control]').forEach((control) => {
        const label = control.labels?.[0] || control.nextElementSibling;
        if (!label?.matches('[data-cielo-variant-tag]')) return;
        const selected = control.checked;
        label.classList.toggle('active', selected);
        label.classList.toggle('is-selected', selected);
        const selectedValue = String(selected);
        if (label.dataset.selected !== selectedValue) {
          label.dataset.selected = selectedValue;
        }
      });
    });
  }

  function buildFeatureStrip(page) {
    const strip = page.querySelector('[data-cielo-feature-strip]');
    if (!strip || strip.dataset.cieloReady === 'true') return;

    const description = document.querySelector('[data-cielo-description-source]') || document.querySelector('.cielo-product-detail-content');
    if (!description) return;

    const descriptionFeatures = structuredFeatures(description);
    const availableFeatures = descriptionFeatures.length ? descriptionFeatures : legacyFeatures(description);
    if (!availableFeatures.length) return;

    availableFeatures.slice(0, 6).forEach((feature) => {
      const item = document.createElement('span');
      item.className = 'cielo-product-feature-strip__item';
      item.dataset.cieloFeatureIcon = feature.icon;
      item.innerHTML = iconMarkup(feature.icon);
      const label = document.createElement('span');
      label.textContent = feature.label;
      item.appendChild(label);
      strip.appendChild(item);
    });

    strip.hidden = false;
    strip.dataset.cieloReady = 'true';
  }

  function getSwitchDetail(value) {
    const normalized = value.replace(/\s*\d+個セット\s*/g, '').trim();
    const setCount = value.match(/(\d+)個(?:セット)?/);
    const isNoSelection = /^(なし|追加しない)$/i.test(normalized);
    const detail = switchDetails.find((candidate) => candidate.pattern.test(normalized)) || {
      shortName: normalized,
      description: '本体と一緒に追加できます。',
      parameters: [],
      imageKey: 'none'
    };
    return {
      ...detail,
      setLabel: setCount ? `${setCount[1]}個セット` : '',
      imageUrl: '',
      isNoSelection
    };
  }

  function getStructuredAppSwitchDetail(control, choice) {
    let specifications = [];
    const specificationSource = choice?.querySelector('[data-cielo-option-specifications]');
    if (specificationSource) {
      try {
        const parsed = JSON.parse(specificationSource.textContent || '[]');
        if (Array.isArray(parsed)) {
          specifications = parsed.filter((item) => typeof item === 'string' && item.trim());
        }
      } catch (_error) {
        specifications = [];
      }
    }
    const includedUnitCount = Number.parseInt(control.dataset.includedUnitCount || '', 10);
    return {
      shortName: control.dataset.optionLabel || control.value,
      description: control.dataset.optionDescription || '本体と一緒に追加できます。',
      parameters: specifications,
      imageKey: 'none',
      imageUrl: control.dataset.optionImageUrl || '',
      isNoSelection: control.dataset.noSelection === 'true',
      setLabel: Number.isSafeInteger(includedUnitCount) && includedUnitCount > 0
        ? `${includedUnitCount}個セット`
        : ''
    };
  }

  function getPrice(label, detail) {
    const match = label.match(/\(\s*\+\s*([\d,]+)円\s*\)/);
    if (match) return `+¥${match[1]}`;
    return detail.isNoSelection ? '+¥0' : '';
  }

  function createSwitchVisual(detail, destination) {
    const visual = document.createElement('span');
    visual.className = 'cielo-key-switch-detail__visual';

    const imageUrl = detail.imageUrl || (detail.imageKey === 'saker'
      ? destination.dataset.sakerImage
      : detail.imageKey === 'panda'
        ? destination.dataset.pandaImage
        : '');

    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 120;
      image.height = 120;
      visual.appendChild(image);
    } else {
      visual.innerHTML = '<svg viewBox="0 0 64 64" aria-hidden="true"><rect x="13" y="14" width="38" height="34" rx="8" fill="none" stroke="currentColor" stroke-width="2"/><rect x="20" y="21" width="24" height="18" rx="4" fill="currentColor" opacity=".16"/><path d="M24 48v5m16-5v5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    }

    return visual;
  }

  function renderSwitchDetail(detailPanel, detail, option, destination, priceOverride = '') {
    const media = document.createElement('span');
    media.className = 'cielo-key-switch-detail__media';
    media.appendChild(createSwitchVisual(detail, destination));

    const price = document.createElement('strong');
    price.className = 'cielo-key-switch-detail__price';
    price.textContent = priceOverride || getPrice(option.label || option.textContent, detail);
    media.appendChild(price);

    const body = document.createElement('span');
    body.className = 'cielo-key-switch-detail__body';

    const heading = document.createElement('span');
    heading.className = 'cielo-key-switch-detail__heading';

    const name = document.createElement('strong');
    name.className = 'cielo-key-switch-detail__name';
    name.textContent = detail.shortName;
    heading.appendChild(name);

    if (detail.setLabel) {
      const setLabel = document.createElement('span');
      setLabel.className = 'cielo-key-switch-detail__set';
      setLabel.textContent = detail.setLabel;
      heading.appendChild(setLabel);
    }

    const description = document.createElement('span');
    description.className = 'cielo-key-switch-detail__description';
    description.textContent = detail.description;

    const parameters = document.createElement('span');
    parameters.className = 'cielo-key-switch-detail__parameters';
    detail.parameters.forEach((parameter) => {
      const value = document.createElement('span');
      value.textContent = parameter;
      parameters.appendChild(value);
    });

    body.append(heading, description);
    if (detail.parameters.length) body.appendChild(parameters);
    detailPanel.replaceChildren(media, body);
  }

  function getAppSwitchPrice(choice, detail) {
    const priceLabel = choice.querySelector('[data-cielo-addon-price-label]');
    const amount = Number(priceLabel?.dataset.price);
    const currency = priceLabel?.dataset.currency || '';
    if (Number.isFinite(amount) && currency) {
      try {
        return `+${new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency,
          maximumFractionDigits: currency === 'JPY' ? 0 : 2
        }).format(amount)}`;
      } catch (_error) {
        return `+${amount.toLocaleString('ja-JP')} ${currency}`;
      }
    }
    return detail.isNoSelection ? '+¥0' : '';
  }

  function syncKeySwitchCopy(destination, sourceGroup, grid) {
    const heading = destination.querySelector('[data-cielo-key-switch-heading]');
    const requirement = destination.querySelector('[data-cielo-key-switch-requirement]');
    const lead = destination.querySelector('[data-cielo-key-switch-lead]');
    const sourceHeading = sourceGroup?.querySelector('.cielo-product-option-group__heading')
      ?.textContent.trim();
    const sourceHelp = sourceGroup?.querySelector('.cielo-product-option-group__help')
      ?.textContent.trim();
    const label = sourceHeading || 'キースイッチセット';
    const required = sourceGroup?.dataset.required === 'true';
    if (heading && heading.textContent !== label) heading.textContent = label;
    if (requirement) {
      const requirementText = required ? '必須' : 'おすすめ';
      if (requirement.textContent !== requirementText) {
        requirement.textContent = requirementText;
      }
      requirement.classList.toggle('is-required', required);
      requirement.classList.toggle('is-recommended', !required);
    }
    if (lead) {
      const usesLegacyHelp = !sourceHelp || legacyKeySwitchHelpTexts.has(sourceHelp);
      const leadText = required
        ? (sourceHelp || `${label}を選択してください。`)
        : (usesLegacyHelp ? keySwitchRecommendedLead : sourceHelp);
      if (lead.textContent !== leadText) lead.textContent = leadText;
    }
    if (grid.getAttribute('aria-label') !== label) {
      grid.setAttribute('aria-label', label);
    }
  }

  function enhanceAppKeySwitchOptions(destination, replacement, grid, detailPanel) {
    const sourceGroup = Array.from(replacement.querySelectorAll('[data-cielo-option-group]'))
      .find((group) => !group.hidden && group.querySelector('input[type="radio"][data-cielo-option-control]:not(:disabled)'));
    const controls = sourceGroup
      ? Array.from(sourceGroup.querySelectorAll('input[type="radio"][data-cielo-option-control]:not(:disabled)'))
        .filter((control) => control.value.trim())
      : [];

    replacement.style.setProperty('display', 'none', 'important');
    replacement.setAttribute('aria-hidden', 'true');
    replacement.dataset.cieloKeySwitchEnhanced = 'true';

    if (!sourceGroup || !controls.length) {
      destination.hidden = true;
      destination.cieloSourceAppGroup = null;
      destination.dataset.cieloOptionSignature = '';
      grid.replaceChildren();
      detailPanel.replaceChildren();
      return false;
    }

    syncKeySwitchCopy(destination, sourceGroup, grid);

    const optionSignature = `${sourceGroup.dataset.groupId || ''}::${controls.map((control) => {
      const choice = control.closest('.cielo-product-option-choice');
      const price = choice?.querySelector('[data-cielo-addon-price-label]');
      const specifications = choice?.querySelector('[data-cielo-option-specifications]')?.textContent || '';
      return `${control.value}::${price?.dataset.price || ''}::${price?.dataset.currency || ''}::${control.dataset.cieloOptionMetadata || ''}::${control.dataset.includedUnitCount || ''}::${control.dataset.optionDescription || ''}::${specifications}`;
    }).join('|')}`;
    if (
      destination.cieloSourceAppGroup === sourceGroup &&
      destination.dataset.cieloOptionSignature === optionSignature &&
      grid.children.length
    ) {
      destination.hidden = false;
      return true;
    }

    destination.cieloSourceAppGroup = sourceGroup;
    destination.cieloSourceSelect = null;
    destination.dataset.cieloOptionSignature = optionSignature;
    sourceGroup.dataset.cieloEnhanced = 'true';
    grid.replaceChildren();
    detailPanel.replaceChildren();

    if (!controls.some((control) => control.checked) && sourceGroup.dataset.required !== 'true') {
      const hasStructuredMetadata = controls.some(
        (control) => control.dataset.cieloOptionMetadata === 'structured'
      );
      const noAddControl = controls.find((control) =>
        control.dataset.noSelection === 'true' ||
        (!hasStructuredMetadata && /^(なし|追加しない)$/i.test(control.value.trim()))
      );
      if (noAddControl) {
        noAddControl.checked = true;
      }
    }

    const optionDetails = new Map();
    const syncSelection = () => {
      const selectedControl = controls.find((control) => control.checked);
      grid.querySelectorAll('[data-cielo-key-switch-value]').forEach((button, index) => {
        const selected = Boolean(selectedControl) && button.dataset.cieloKeySwitchValue === selectedControl.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected || (!selectedControl && index === 0) ? 0 : -1;
      });

      if (!selectedControl) {
        detailPanel.replaceChildren();
        return;
      }

      const selectedItem = optionDetails.get(selectedControl.value);
      if (selectedItem) {
        renderSwitchDetail(
          detailPanel,
          selectedItem.detail,
          selectedItem.option,
          destination,
          selectedItem.price
        );
      }
    };
    destination.cieloSyncSelection = syncSelection;

    controls.forEach((control) => {
      const choice = control.closest('.cielo-product-option-choice');
      const detail = control.dataset.cieloOptionMetadata === 'structured'
        ? getStructuredAppSwitchDetail(control, choice)
        : getSwitchDetail(control.value);
      const option = { label: choice?.textContent || control.value };
      optionDetails.set(control.value, {
        detail,
        option,
        price: choice ? getAppSwitchPrice(choice, detail) : ''
      });

      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = 'cielo-key-switch-tag';
      tag.dataset.cieloKeySwitchValue = control.value;
      tag.setAttribute('role', 'radio');
      tag.textContent = detail.shortName;
      tag.setAttribute('aria-label', detail.setLabel ? `${detail.shortName} ${detail.setLabel}` : detail.shortName);

      tag.addEventListener('click', () => {
        control.checked = true;
        syncSelection();
      });

      tag.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const tags = Array.from(grid.querySelectorAll('[data-cielo-key-switch-value]'));
        const currentIndex = tags.indexOf(tag);
        const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
        const next = tags[(currentIndex + direction + tags.length) % tags.length];
        next.click();
        next.focus();
      });

      if (control.dataset.cieloKeySwitchBridge !== 'true') {
        control.dataset.cieloKeySwitchBridge = 'true';
        control.addEventListener('change', () => destination.cieloSyncSelection?.());
      }
      grid.appendChild(tag);
    });

    syncSelection();
    destination.hidden = false;
    return true;
  }

  function enhanceKeySwitchOptions(page) {
    const destination = page.querySelector('[data-cielo-key-switch-options]');
    const grid = destination?.querySelector('[data-cielo-key-switch-grid]');
    const detailPanel = destination?.querySelector('[data-cielo-key-switch-detail]');
    if (!destination || !grid || !detailPanel) return false;

    const productOptionsReplacement = page.querySelector(
      '[data-cielo-product-options-root][data-display-mode="key_switch"]'
    );
    if (productOptionsReplacement) {
      if (productOptionsReplacement.dataset.cieloOptionsReady !== 'true') {
        destination.hidden = true;
        destination.cieloSourceAppGroup = null;
        destination.dataset.cieloOptionSignature = '';
        grid.replaceChildren();
        detailPanel.replaceChildren();
        return false;
      }
      return enhanceAppKeySwitchOptions(destination, productOptionsReplacement, grid, detailPanel);
    }
    const select = page.querySelector(keySwitchSelector);
    if (!select) return false;

    syncKeySwitchCopy(destination, null, grid);

    const sourceGroup = select.closest('.gpo-element');
    const options = Array.from(select.options).filter((option) => !option.disabled && option.value.trim());
    if (!sourceGroup || !options.length) return false;
    const optionSignature = options.map((option) => `${option.value}::${option.label || option.textContent}`).join('|');
    if (
      destination.cieloSourceSelect === select &&
      destination.dataset.cieloOptionSignature === optionSignature &&
      grid.children.length &&
      detailPanel.children.length
    ) return true;

    sourceGroup.hidden = true;
    sourceGroup.dataset.cieloEnhanced = 'true';
    destination.cieloSourceSelect = select;
    destination.dataset.cieloOptionSignature = optionSignature;
    grid.replaceChildren();
    detailPanel.replaceChildren();

    if (!select.value) {
      const noAddOption = options.find((option) => /^(なし|追加しない)$/i.test(option.value.trim())) || options[0];
      select.value = noAddOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const optionDetails = new Map();

    const syncSelection = () => {
      grid.querySelectorAll('[data-cielo-key-switch-value]').forEach((button) => {
        const selected = button.dataset.cieloKeySwitchValue === select.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });

      const selectedItem = optionDetails.get(select.value) || optionDetails.values().next().value;
      if (selectedItem) {
        renderSwitchDetail(detailPanel, selectedItem.detail, selectedItem.option, destination);
      }
    };

    options.forEach((option) => {
      const detail = getSwitchDetail(option.value);
      optionDetails.set(option.value, { detail, option });

      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = 'cielo-key-switch-tag';
      tag.dataset.cieloKeySwitchValue = option.value;
      tag.setAttribute('role', 'radio');
      tag.textContent = detail.shortName;
      tag.setAttribute('aria-label', detail.setLabel ? `${detail.shortName} ${detail.setLabel}` : detail.shortName);

      tag.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncSelection();
      });

      tag.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const tags = Array.from(grid.querySelectorAll('[data-cielo-key-switch-value]'));
        const currentIndex = tags.indexOf(tag);
        const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
        const next = tags[(currentIndex + direction + tags.length) % tags.length];
        next.click();
        next.focus();
      });

      grid.appendChild(tag);
    });

    select.addEventListener('change', syncSelection);
    syncSelection();
    destination.hidden = false;
    return true;
  }

  function initPage(page) {
    if (page.dataset.cieloProductPageReady === 'true') return;
    page.dataset.cieloProductPageReady = 'true';
    buildFeatureStrip(page);
    syncVariantTags(page);
    enhanceKeySwitchOptions(page);

    const observer = new MutationObserver(() => {
      buildFeatureStrip(page);
      syncVariantTags(page);
      enhanceKeySwitchOptions(page);
    });
    observer.observe(page, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);

    const variantSelector = page.querySelector('variant-radios, variant-selects');
    variantSelector?.addEventListener('change', () => {
      syncVariantTags(page);
      requestAnimationFrame(() => enhanceKeySwitchOptions(page));
    });
    page.addEventListener('cielo:product-options:conditions-updated', () => {
      enhanceKeySwitchOptions(page);
    });
    page.addEventListener('cielo:product-options:ready', () => {
      enhanceKeySwitchOptions(page);
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll(pageSelector).forEach(initPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
